/**
 * Typed client for klapzo-live-api. Wire types mirror the server protocol
 * (klapzo-live-api/src/realtime/protocol.ts + live/types.ts), kept in sync by
 * hand. In production the API is same-site at api.klapzo.app, so the session
 * cookie (Domain=.klapzo.app, SameSite=Lax) rides along on credentialed calls.
 */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.klapzo.app";

export type QuestionType = "mc" | "wordcloud" | "open" | "promptvote" | "picker";
/** picker: what a draw produces. Mirrors live/types.ts `PickMode`. */
export type PickMode = "one" | "weighted" | "teams" | "rotation";
/**
 * mc: what an ANSWER is (W9a). Mirrors live/types.ts `McPayload`. Absent = `one`, which is every
 * poll and quiz shipped before this — the wire for those did not change.
 */
export type McPayload = "one" | "weighted" | "ordered";
export interface McOption {
  id: string;
  label: string;
  /**
   * The scale point this option STANDS FOR (W9b). Mirrors live/types.ts `McOption.value`: read only
   * by an `aggregate: "mean"` round, where every option must carry one, and inert everywhere else.
   */
  value?: number;
}
/** mc: how a correct answer scores. Mirrors live/scoring.ts. */
export type ScoringMode = "speed" | "accuracy" | "self-paced";
export interface ScoringConfig {
  mode: ScoringMode;
  points?: number;
}
export interface QuestionConfig {
  id: string;
  type: QuestionType;
  prompt: string;
  options?: McOption[];
  scored?: boolean;
  correctOptionIds?: string[];
  timeLimitMs?: number;
  /** mc: how the room answers (W9a). `weighted` REQUIRES `budget`; neither may carry a score. */
  payload?: McPayload;
  /** mc `weighted`: points each person distributes. The API takes an integer 1..100. */
  budget?: number;
  /**
   * mc: what the round's numbers MEAN (W9b). Absent = `sum` = the histogram every poll shipped
   * before this; `mean` makes the headline the AVERAGE of the options' `value`s. Never scored, and
   * `payload` `one` only — the API refuses the rest by name.
   */
  aggregate?: "sum" | "mean";
  /**
   * mc: a SINGLE-SCREEN round (US-LV-22, "sem celular"). The host taps the room's choice; no phone
   * answers and no participant datum exists. `true` is the only value; the API refuses it next to a
   * score, payload, clock or targeting.
   */
  hosted?: true;
  /** mc: `speed` (decay, legacy default when absent) | `accuracy` (flat) | `self-paced` (flat, no clock). */
  scoring?: ScoringConfig;
  locale?: "pt-BR" | "en-US" | "es-LATAM";
  /**
   * wordcloud: how many WORDS one person may put in the cloud (US-LV-20). Absent = unlimited.
   * Counted in words, not submissions — one frame of "azul verde" spends two.
   */
  maxWords?: 1 | 2 | 3;
  allowUpvote?: boolean;
  /** open: hold submissions in a host review queue instead of publishing on arrival. */
  preModerate?: boolean;
  /** open: at most ONE entry per person (US-LV-20). Absent = many; `1` is the only value. */
  maxEntries?: 1;
  /**
   * open: a NAMED PANEL — the author's nickname stands beside the sentence (US-LV-20). Absent =
   * the anonymous wall. The API refuses it on any other type.
   */
  named?: boolean;
  /** promptvote: `timeLimitMs` bounds the write phase, this one the vote phase. */
  voteTimeLimitMs?: number;
  /**
   * mc / wordcloud / open: withhold the live result until the round closes or the host reveals
   * (US-LV-20). The withheld arm still carries `total`/`respondents` and an EMPTY `words`/`entries`
   * list. Refused on `promptvote` (its phases ARE the withholding) and `picker` (no tally).
   */
  revealTally?: boolean;
  /** promptvote: name the authors at reveal. `false` also forces the round unscored. */
  revealAuthors?: boolean;
  /** picker: what to draw. Absent = `one`. On a picker round `timeLimitMs` is the SPIN duration. */
  pick?: PickMode;
  /** picker: how many teams. `teams` only; the API requires an integer 2..20. */
  k?: number;
  /** picker: draw from these host-typed names instead of the room. ≤100, ≤48 chars each. */
  pool?: string[];
  /** mc/open: per-recipient overrides (W6a). Never on an inbound frame — see `PublicQuestion`. */
  variants?: QuestionVariant[];
  /**
   * mc/open/wordcloud/promptvote: the ONLY people who may answer this round (W7). Absent = everyone;
   * the API refuses an empty array, and refuses the field entirely on a `picker`. Never on an inbound
   * frame — it names other people's pids, so the server strips it and sends `readOnly` instead.
   */
  eligible?: string[];
}
/**
 * One targeted slice of a round (W6a, US-LV-13). Mirrors live/types.ts `QuestionVariant`.
 *
 * `pids` are the NON-secret public ids from `GET /sessions/:id/participants`; a participant token
 * never appears here. `options` must draw its ids from the base option set — the API refuses
 * anything else, because an invented id would score against nothing and land in a bar nobody sees.
 */
export interface QuestionVariant {
  pids: string[];
  prompt?: string;
  options?: McOption[];
}
/**
 * A question as it arrives on THIS client's socket. The server strips the answer key and the whole
 * targeting table before sending, so neither type exists here to read; `private` is set only on a
 * payload merged for this recipient and drives the "só você vê isso" badge.
 */
export type PublicQuestion = Omit<QuestionConfig, "correctOptionIds" | "variants" | "eligible"> & {
  private?: true;
  /**
   * This round is not taking THIS phone's answer (W7). Set by the server only on the copy sent to a
   * participant the round's `eligible` list leaves out — never on the projection's payload, which was
   * never answering anything. Backed by a server refusal (`NOT_ELIGIBLE`), so disabling the controls
   * is a courtesy, not the guard.
   */
  readOnly?: true;
  /**
   * When this round closes, as an absolute instant — only in a long-running room (W11), and only
   * when the round has a deadline at all.
   *
   * `timeLimitMs` is a duration from an open this phone may not have witnessed, so a participant
   * arriving on Wednesday for a round opened on Monday cannot derive Friday 18:00 from it. Rendered
   * as a DATE in the phone's own timezone (`closesOn`), never as a five-day countdown.
   */
  closesAtMs?: number;
};

/**
 * How the NEXT round is built out of the one that just closed (W8a). Mirrors live/types.ts.
 *
 * `pick` is an enum with one value: the bracket derivation is `"pairs"` here, not a second endpoint.
 * `prompt` is always the host's — the source round asked a different question.
 */
export interface DeriveSpec {
  pick: "top";
  /** How many answers carry over. The API takes an integer 2..10 and clamps to what the room has. */
  k: number;
  prompt: string;
  /** Absent = untimed, and the host closes the final by hand. */
  timeLimitMs?: number;
  /**
   * The armed floor (W7) and briefs (W6a) for the final, carried onto the derived round and
   * validated by the API exactly like a typed round's — same codes. A final that dropped them would
   * apply them to the round AFTER it, while the panel on screen said they applied now.
   */
  eligible?: string[];
  variants?: QuestionVariant[];
}

/** One team in the room (W7). Mirrors live/types.ts `TeamConfig`. `pids` are host-side only. */
export interface TeamConfig {
  id: string;
  name: string;
  pids: string[];
}

/**
 * The room's team layer. `mean` averages over the members who already hold a score, which is what
 * stops one fast player carrying the team. `secret` changes the HOST's screen only — the wire never
 * carries membership either way.
 */
export interface TeamSet {
  aggregate: "sum" | "mean";
  secret: boolean;
  teams: TeamConfig[];
}

/** One row of the broadcast team board: name, score, rank, size — never who is on it. */
export interface TeamStanding {
  id: string;
  name: string;
  score: number;
  rank: number;
  size: number;
}

/** This phone's own place in the room (W7), addressed to it and to nobody else. */
export interface MyBoard {
  teamRank: number;
  teamCount: number;
  rankInTeam: number;
  teamSize: number;
  /** 0 when this participant has no score row yet — not "last". */
  globalRank: number;
}

export interface WordCount {
  word: string;
  count: number;
}
export interface OpenEntryView {
  id: string;
  text: string;
  upvotes: number;
  highlighted: boolean;
  /**
   * Who wrote it, on a `named` panel round only (US-LV-20). A NICKNAME — the server never puts a
   * token or a pid on any frame. Absent on every anonymous round, which is every round shipped
   * before this story, so a renderer cannot print a name by forgetting a flag.
   */
  author?: string;
}
export interface BallotEntry {
  id: string;
  text: string;
}
export interface AnonResult extends BallotEntry {
  votes: number;
}
export interface PromptResult extends AnonResult {
  nickname: string;
}
/**
 * The promptvote arms are split by phase on purpose (mirroring the server): during `write` there is
 * no ballot field to render, and before `reveal` there is no author to name. Rendering either too
 * early is a compile error here too, not something a reviewer has to catch. The reveal splits again
 * on `authors`, so an anonymous round has no `nickname` in the type at all — a renderer cannot
 * print one by forgetting a flag.
 */
/** A round as the results endpoint returns it. Mirrors RoundRecord server-side. */
export interface RoundResult {
  sessionId: string;
  tenantId: string;
  ord: number;
  questionId: string;
  type: "mc" | "wordcloud" | "open" | "promptvote";
  prompt: string;
  scored: boolean;
  responses: number;
  aggregate: QuestionAggregate;
  openedAt: number;
  closedAt: number;
}

export interface SessionResults {
  sessionId: string;
  plan: string;
  /** The server decides; the client never derives entitlement from the plan name. */
  canExport: boolean;
  retentionDays: number;
  /** Rounds the plan's retention window put out of reach. Shown, not hidden. */
  withheldByRetention: number;
  summary: {
    rounds: number;
    totalResponses: number;
    /** Most answers any single round drew. NOT a count of people in the room. */
    peakResponses: number;
    firstRoundAt: number | null;
    lastRoundAt: number | null;
  };
  rounds: RoundResult[];
}

export type QuestionAggregate =
  // `payload` is absent on a `one` round. When present, `counts` is points (a sum, or a Borda
  // score) rather than votes — which is the only thing the screens change: the unit label.
  // `aggregate` is absent unless the round asked for an average (W9b) — `counts` and `total` keep
  // meaning votes per scale point and respondents, and the mean is read off them by `meanOf`.
  // `options` rides the PERSISTED round only (live-api attaches it at close), which is what lets
  // this app print a label instead of `o7` a day later.
  | {
      type: "mc";
      tally: "open";
      counts: Record<string, number>;
      total: number;
      revealed: boolean;
      payload?: "weighted" | "ordered";
      aggregate?: "mean";
      /** The tally is the HOST's recorded choice for the room, not participants' answers (US-LV-22). */
      hosted?: true;
      options?: McOption[];
      correctOptionIds?: string[];
    }
  | { type: "mc"; tally: "hidden"; total: number; revealed: boolean }
  // `total` is WORDS and `respondents` is PEOPLE — see `answeredIn`, which is why the difference
  // matters. Optional: a frame from a Worker deployed before the round-1 review carries neither.
  //
  // `revealed` rides these two arms ONLY on a round that set `revealTally: false` — see
  // `isWithheldList`, which is the only reader, and the server's own arm for why it is conditional.
  | { type: "wordcloud"; words: WordCount[]; total: number; respondents?: number; revealed?: boolean }
  | { type: "open"; entries: OpenEntryView[]; pendingCount: number; respondents?: number; revealed?: boolean }
  | { type: "promptvote"; phase: "write"; submissions: number }
  // `omitted` = votable entries the ballot could not hold (W10a). Absent when everything fit, so a
  // round under the cap renders exactly as it did before. The texts are not here and never were.
  | { type: "promptvote"; phase: "vote"; ballot: BallotEntry[]; votesCast: number; omitted?: number }
  | { type: "promptvote"; phase: "reveal"; authors: "named"; results: PromptResult[]; omitted?: number }
  | { type: "promptvote"; phase: "reveal"; authors: "hidden"; results: AnonResult[]; omitted?: number }
  // picker, phase-split like promptvote: while the wheel is spinning there is no result field to
  // render early — a screen cannot leak what its type does not have.
  | { type: "picker"; phase: "spinning"; pick: PickMode; candidates: string[] }
  | { type: "picker"; phase: "drawn"; pick: "one" | "weighted"; candidates: string[]; drawn: string | null }
  | { type: "picker"; phase: "drawn"; pick: "teams"; candidates: string[]; teams: string[][] }
  | { type: "picker"; phase: "drawn"; pick: "rotation"; candidates: string[]; order: string[] };

/**
 * Reveal rows flattened for rendering — `author` is null on an anonymous round.
 *
 * The narrowing happens HERE, once, because `.map()` over a union of arrays widens the element back
 * to `AnonResult | PromptResult` and every screen would otherwise re-derive it (and one of them
 * would eventually reach for `nickname` behind a flag check TypeScript can't follow).
 */
/**
 * What a bar's 100% means (W9a).
 *
 * Respondents on a vote — one person, one bar, and the percentages add to 100. On a payload round
 * they do NOT: three people with a 100-point budget put up to 300 points on the table, so dividing
 * by the respondent count renders a 300%-wide bar off the side of the projector. The denominator
 * there is the points actually placed.
 */
export function barTotal(agg: Extract<QuestionAggregate, { type: "mc"; tally: "open" }>): number {
  if (agg.payload === undefined) return agg.total;
  return Object.values(agg.counts).reduce((a, b) => a + b, 0);
}

/**
 * The room's average (W9b, US-LV-5) — the one number a scale round is for.
 *
 * Σ counts[o] · value(o) / total, over RESPONDENTS: a mean round is a `one` payload, so the votes
 * and the people are the same number and `barTotal`'s payload branch never applies here.
 *
 * `null`, never 0 or NaN, in the two cases where there is no average to state: nobody answered, and
 * a VOTED option with no `value` — a round closed before this wave (its record has no option table
 * at all) or one whose options were edited afterwards. An option nobody chose cannot spoil it.
 *
 * Mirrored from live-api's `meanOf` in `live/types.ts` by hand, like every wire type in this file
 * and like the join-code alphabet below: nothing here imports from the Worker source.
 */
export function meanOf(counts: Record<string, number>, options: readonly McOption[] | undefined, total: number): number | null {
  if (total <= 0) return null;
  let sum = 0;
  for (const [id, n] of Object.entries(counts)) {
    if (n === 0) continue;
    const value = options?.find((o) => o.id === id)?.value;
    if (!Number.isFinite(value)) return null;
    sum += n * (value as number);
  }
  return sum / total;
}

/**
 * An option's label as the CLOSED ROUND recorded it (W9b), or null when it did not record one.
 *
 * Null rather than the id, because the two readers fall back differently and both are right: the
 * results screen has nothing else on file and prints the id, while the planner still holds the card
 * that opened the round and prints the card's word. A round closed before this wave persisted no
 * option table at all, and nothing backfills one.
 */
export function labelOf(options: readonly McOption[] | undefined, optionId: string): string | null {
  const label = options?.find((o) => o.id === optionId)?.label;
  return typeof label === "string" && label !== "" ? label : null;
}

/** One session's point on a trend line: aggregates only (US-LV-23 AC4). */
export interface SeriesPoint {
  sessionId: string;
  closedAt: number;
  mean: number;
  respondents: number;
}
export interface SeriesLine {
  key: string;
  prompt: string;
  points: SeriesPoint[];
}
export interface SeriesTrend {
  seriesId: string;
  sessions: number;
  withheldPoints: number;
  lines: SeriesLine[];
}

/**
 * The series label as the API accepts it (mirrors `parseSeriesId` in the Worker): trimmed, 1–64 of
 * `[A-Za-z0-9_-]`. `null` = send none, so a typo in an optional field never blocks "criar sessão"
 * with a refusal the host cannot see; the field shows its own hint instead (`isSeriesIdValid`).
 */
export function normalizeSeriesId(raw: string): string | null {
  const v = raw.trim();
  return /^[A-Za-z0-9_-]{1,64}$/.test(v) ? v : null;
}

/** Empty is fine (no series); non-empty must be a valid label. */
export function isSeriesIdValid(raw: string): boolean {
  return raw.trim() === "" || normalizeSeriesId(raw) !== null;
}

/**
 * Where each point of a line sits in a `w`×`h` box, padding `pad` on every side. Pure so the
 * geometry is tested and the SVG only maps it. The y range is the line's own min..max (a flat line
 * sits in the middle), because a 1–5 scale mean moving 3.8 → 4.1 is invisible on a fixed 0–5 axis.
 */
export function trendGeometry(means: readonly number[], w: number, h: number, pad = 8): Array<{ x: number; y: number }> {
  if (means.length === 0) return [];
  const lo = Math.min(...means);
  const hi = Math.max(...means);
  const innerW = w - 2 * pad;
  const innerH = h - 2 * pad;
  return means.map((m, i) => ({
    x: means.length === 1 ? w / 2 : pad + (innerW * i) / (means.length - 1),
    y: hi === lo ? h / 2 : pad + innerH * (1 - (m - lo) / (hi - lo)),
  }));
}

/**
 * A mean, in the reader's own language: one decimal, and the locale's own separator ("7,2" / "7.2").
 *
 * One decimal because the mean of eleven integers is a headline, not a measurement — and because
 * "7,23809" reads as precision the room did not produce. The CSV formats its own (a dot, always):
 * a spreadsheet is not a localized surface.
 */
export function formatMean(mean: number, locale: string): string {
  return new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(mean);
}

/**
 * The join-code alphabet, mirrored from the API's `sessions/codes.ts` (W10a, US-LV-17 AC2).
 *
 * Copied, not imported: the web bundle does not pull from the Worker source, and there is no shared
 * package between them yet. It is 31 characters that have not changed since the first session was
 * ever created — if a second consumer appears, THAT is the moment to extract one.
 */
const JOIN_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; // no 0 O 1 I L
const JOIN_CODE_LENGTH = 6;

/**
 * What a person typed → the code to navigate to, or `null` if it cannot be one.
 *
 * Forgiving about how it was typed (lower case off a phone keyboard, the spaces and hyphens people
 * add reading six characters off a wall) and strict about what it IS — a wrong code should reach
 * the join screen's own refusal, but a code with a letter that cannot exist should never cost a
 * round-trip to find that out.
 */
export function normalizeJoinCode(input: string): string | null {
  const code = input.replace(/[\s-]/g, "").toUpperCase();
  if (code.length !== JOIN_CODE_LENGTH) return null;
  for (const ch of code) if (!JOIN_CODE_ALPHABET.includes(ch)) return null;
  return code;
}

/**
 * What a projection list shows, and how much it is not showing (W10a, US-LV-17 AC4).
 *
 * The rows arrive already in the order the room should read them (votes or upvotes first), so this
 * only cuts — never re-sorts. `more` is what the screen must SAY: a list silently truncated at the
 * projector is the same trust failure as a ballot silently truncated at the server.
 *
 * `pin` survives the cut. A host who highlights a Q&A entry is doing exactly one thing — putting it
 * on the wall — and the server orders Q&A by UPVOTES alone, so "let's take this one" on a question
 * nobody upvoted sorted it to last place and the cap then deleted it. The highlight would have been
 * visible on the host's uncapped panel and nowhere else, which is the one outcome the control
 * cannot have. Pinned rows keep their relative order and come first; the rest fill the cap.
 *
 * The phone does not use this. A phone scrolls.
 */
export function visibleRows<T>(rows: T[], cap = 20, pin?: (row: T) => boolean): { shown: T[]; more: number } {
  if (!pin) {
    if (rows.length <= cap) return { shown: rows, more: 0 };
    return { shown: rows.slice(0, cap), more: rows.length - cap };
  }
  // Applied at EVERY length, not only past the cap: skipping it under the cap left a highlighted
  // 0-upvote row sitting last with twenty entries and jumping to the top the moment a
  // twenty-first arrived — the wall reordering itself because someone else asked a question.
  const pinned = rows.filter(pin);
  const rest = rows.filter((r) => !pin(r));
  // `Math.max(0, …)`: a host who highlighted more than the cap gets all of them and no filler —
  // they asked for those rows on the wall, and silently dropping some is the bug being fixed.
  const shown = [...pinned, ...rest.slice(0, Math.max(0, cap - pinned.length))];
  return { shown, more: rows.length - shown.length };
}

export function revealRows(
  agg: Extract<QuestionAggregate, { phase: "reveal" }>,
): Array<{ id: string; text: string; votes: number; author: string | null }> {
  return agg.authors === "named"
    ? agg.results.map((r) => ({ id: r.id, text: r.text, votes: r.votes, author: r.nickname }))
    : agg.results.map((r) => ({ id: r.id, text: r.text, votes: r.votes, author: null }));
}

// No participant token: the server never puts one on the wire (it is a bearer credential and these
// frames reach every socket). Rows are positional — `rank` is not unique on a tie, so key by index.
export interface LeaderboardEntry {
  nickname: string;
  score: number;
  rank: number;
}

export type ServerEvent =
  // The room's join code (W10a) — public by construction, which is why it rides the frame every
  // socket already gets. "" when the room has not been told it yet; render nothing, never a QR of a
  // code nobody can join with.
  | { t: "state"; status: "draft" | "live" | "closed"; participants: number; currentQuestionId: string | null; joinCode: string }
  | { t: "joined"; participants: number }
  | { t: "left"; participants: number }
  | { t: "question"; question: PublicQuestion }
  | { t: "aggregate"; questionId: string; aggregate: QuestionAggregate }
  | { t: "closed"; questionId: string }
  // `teams` rides the same event as the individual board — they are one picture. Absent means the
  // room has no team layer, which is also how this client learns one was cleared.
  | { t: "leaderboard"; entries: LeaderboardEntry[]; teams?: TeamStanding[] }
  // Addressed to this socket alone: the only frame that carries membership, and only its own.
  | { t: "team"; id: string; name: string }
  // The micro-leaderboard, likewise addressed. Ranks only — no nickname, no roster.
  | { t: "my_board"; teamRank: number; teamCount: number; rankInTeam: number; teamSize: number; globalRank: number }
  // Trivia race: which round is live (1-based) out of how many, and whether the race is over.
  | { t: "race"; round: number; total: number; ended: boolean }
  | { t: "error"; code: string };

async function postJson<T>(path: string, body?: unknown): Promise<{ status: number; data: T }> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body ?? {}),
  });
  return { status: res.status, data: (await res.json().catch(() => ({}))) as T };
}

export interface MeResult {
  authenticated: boolean;
  role?: "owner" | "host" | "member";
  name?: string | null;
}

export const api = {
  wsUrl(connectPath: string): string {
    const base = API_BASE.replace(/^http/, "ws");
    return `${base}${connectPath}`;
  },
  /** Request a magic-link email. Always resolves ok (no account enumeration). */
  magicRequest(email: string): Promise<{ status: number; data: { ok?: boolean; error?: string } }> {
    return postJson("/auth/magic/request", { email });
  },
  /** Current session (for route guards). 401 => not authenticated. */
  async me(): Promise<MeResult> {
    const res = await fetch(`${API_BASE}/auth/me`, { credentials: "include" });
    if (res.status !== 200) return { authenticated: false };
    return (await res.json().catch(() => ({ authenticated: false }))) as MeResult;
  },
  login(email: string, password: string): Promise<{ status: number; data: { ok?: boolean; role?: string } }> {
    return postJson("/auth/login", { email, password });
  },
  logout(): Promise<{ status: number; data: unknown }> {
    return postJson("/auth/logout");
  },
  devLogin(): Promise<{ status: number; data: unknown }> {
    return postJson("/dev/login", { sub: "dev-host", tid: "dev-tenant", role: "host", name: "Facilitador Dev" });
  },
  aiQuiz(topic: string, count: number, locale: string): Promise<{ status: number; data: { drafts?: QuestionConfig[]; error?: string } }> {
    return postJson("/ai/quiz", { topic, count, locale });
  },
  /**
   * `longRunning` buys the tier's own horizon instead of 12 h (W11). Refused with 402
   * `ASYNC_NOT_ON_PLAN` on a plan that does not sell it — which `asLimitError` has to recognise, or
   * the host clicks "criar sessão", nothing changes on screen, and they click again.
   */
  createSession(opts: { longRunning?: boolean; seriesId?: string } = {}): Promise<{
    status: number;
    data: { sessionId: string; joinCode: string; qr: string; joinUrl: string; expiresAt: number };
  }> {
    const series = normalizeSeriesId(opts.seriesId ?? "");
    return postJson("/sessions", {
      ...(opts.longRunning === true ? { longRunning: true } : {}),
      ...(series !== null ? { seriesId: series } : {}),
    });
  },
  /** The trend of one series (W13, US-LV-23). Host-only, tenant-scoped on the server. */
  async seriesTrend(seriesId: string): Promise<{ status: number; data: SeriesTrend | null }> {
    const res = await fetch(`${API_BASE}/series/${encodeURIComponent(seriesId)}/trend`, { credentials: "include" });
    if (!res.ok) return { status: res.status, data: null };
    return { status: res.status, data: (await res.json()) as SeriesTrend };
  },
  // `expiresAt` (unix seconds) rides along for a long-running room only — it is what the join screen
  // needs to STATE the retention period AC7 asks for, and a period nobody can see is a
  // purpose-limitation problem rather than a PR note.
  resolveCode(code: string): Promise<{ status: number; data: { sessionId: string; status: string; connectPath: string; longRunning?: boolean; expiresAt?: number } }> {
    return fetch(`${API_BASE}/j/${code}`).then(async (r) => ({ status: r.status, data: (await r.json().catch(() => ({}))) as never }));
  },
  join(
    code: string,
    nickname: string,
  ): Promise<{
    status: number;
    // `error` is present on the 4xx arms (NICKNAME_REQUIRED / NICKNAME_LOOKS_LIKE_PII / …); the join
    // screen needs to tell those apart, so it is not optional-by-omission.
    // `pid` is this participant's public id (W6a). This response is the only place it arrives —
    // no frame carries a pid, and no frame carries a token.
    // `longRunning` (W11) decides which STORE keeps the seat: a room that runs for days needs it to
    // survive the tab closing, and an ordinary one must not outlive the tab on a shared phone.
    data: { sessionId?: string; participantToken?: string; pid?: string; connectPath?: string; error?: string; longRunning?: boolean; expiresAt?: number };
  }> {
    return postJson(`/j/${code}/join`, { nickname });
  },
  openQuestion(sessionId: string, config: QuestionConfig): Promise<{ status: number; data: unknown }> {
    return postJson(`/sessions/${sessionId}/questions`, config);
  },
  /** `optionId` is the choice a host records for the room on a `hosted` round (US-LV-22). */
  closeQuestion(sessionId: string, optionId?: string): Promise<{ status: number; data: unknown }> {
    return postJson(`/sessions/${sessionId}/questions/close`, optionId === undefined ? undefined : { optionId });
  },
  /**
   * Open a round built from the one that just closed (W8a) — a run-off, a final, the top words.
   *
   * One call, not two: the server derives AND opens, because the derived config never has to exist
   * on this client. `options` comes back so the host can see what was derived without waiting for
   * the broadcast — this is the one round whose content they did not type.
   *
   * A 400 carries the refusal code: the round's own (`SOURCE_NOT_CLOSED` / `CANNOT_DERIVE_FROM_TYPE` /
   * `NOT_ENOUGH_CANDIDATES`), the spec's (`INVALID_DERIVE` / `INVALID_DERIVE_K` / `INVALID_DERIVE_TIME` /
   * `MISSING_FIELDS`), or — because the armed floor and brief ride along — any `eligible`/`variants`
   * code `POST /questions` can answer. 402 and 409 are the usual quota and ended-room answers.
   */
  deriveRound(
    sessionId: string,
    spec: DeriveSpec,
  ): Promise<{ status: number; data: { questionId?: string; options?: McOption[]; error?: string } }> {
    return postJson(`/sessions/${sessionId}/questions/derive`, spec);
  },
  /** Load an ordered list of scored+timed rounds for a trivia race (does not start it). */
  loadRace(sessionId: string, questions: QuestionConfig[]): Promise<{ status: number; data: { rounds?: number; error?: string } }> {
    return postJson(`/sessions/${sessionId}/race`, { questions });
  },
  /** Advance the race: close the open round and open the next, or end it and reveal the final board. */
  nextRound(sessionId: string): Promise<{ status: number; data: unknown }> {
    return postJson(`/sessions/${sessionId}/race/next`);
  },
  revealQuestion(sessionId: string): Promise<{ status: number; data: unknown }> {
    return postJson(`/sessions/${sessionId}/questions/reveal`);
  },
  /**
   * Host moderation of an entry. The route existed server-side but nothing called it, so open-Q&A
   * moderation (US-LV-4) was unreachable from the UI — wiring it here fixes that too. On a
   * `promptvote` round only `hidden` applies: it drops the entry from the ballot and from the score.
   */
  /** The Q&A review queue. Host-only on the server; unapproved text never rides the broadcast. */
  async pending(sessionId: string): Promise<Array<{ id: string; text: string }>> {
    const res = await fetch(`${API_BASE}/sessions/${sessionId}/pending`, { credentials: "include" });
    if (!res.ok) return [];
    const body = (await res.json().catch(() => ({}))) as { entries?: Array<{ id: string; text: string }> };
    return body.entries ?? [];
  },
  /**
   * Who is in the room, by public id — the host's targeting roster (W6a). Host-only on the server,
   * and deliberately not on the socket: pids are off the wire, so this read is the only way a host
   * learns who they can address.
   */
  /** The host roster, or null when the read failed — an empty room and a failed read are different facts. */
  async participants(sessionId: string): Promise<Array<{ pid: string; nickname: string }> | null> {
    const res = await fetch(`${API_BASE}/sessions/${sessionId}/participants`, { credentials: "include" });
    if (!res.ok) return null;
    const body = (await res.json().catch(() => ({}))) as { participants?: Array<{ pid: string; nickname: string }> };
    return body.participants ?? [];
  },
  /**
   * Replace the room's team layer (W7). `null` clears it — which the API spells `{ teams: [] }`, so
   * that a cleared layer and a mis-built one cannot arrive as the same request.
   */
  setTeams(sessionId: string, set: TeamSet | null): Promise<{ status: number; data: { ok?: boolean; error?: string } }> {
    return fetch(`${API_BASE}/sessions/${sessionId}/teams`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify(set ?? { teams: [] }),
    }).then(async (r) => ({ status: r.status, data: (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string } }));
  },
  /**
   * The room's teams, host-only. Undefined when the read failed — an empty room, a room with no teams
   * and a failed read are three different facts, and only the last must not overwrite a live panel.
   */
  async teams(sessionId: string): Promise<TeamSet | null | undefined> {
    const res = await fetch(`${API_BASE}/sessions/${sessionId}/teams`, { credentials: "include" });
    if (!res.ok) return undefined;
    const body = (await res.json().catch(() => ({}))) as { teams?: TeamSet | null };
    return body.teams ?? null;
  },
  /**
   * "Agrupar por IA" (US-LV-21): on demand, host-only. The groups are ids and labels — the texts are
   * already on the host's screen — and nothing is stored or shown to the room.
   */
  groupOpen(
    sessionId: string,
    locale: string,
  ): Promise<{ status: number; data: { groups?: Array<{ label: string; entryIds: string[] }>; skipped?: number; error?: string } }> {
    return postJson(`/sessions/${sessionId}/questions/group`, { locale });
  },
  moderate(
    sessionId: string,
    entryId: string,
    change: { hidden?: boolean; highlighted?: boolean; approved?: boolean },
  ): Promise<{ status: number; data: { aggregate?: QuestionAggregate; error?: string } }> {
    return postJson(`/sessions/${sessionId}/moderate`, { entryId, ...change });
  },
  /**
   * The download URL. Not a fetch: the browser follows it so `Content-Disposition` does its job and
   * the file lands in Downloads instead of in memory. `credentials: include` has no meaning for a
   * navigation, but the session cookie is `Domain=.klapzo.app`, so it rides along anyway.
   */
  exportUrl(sessionId: string, format: "csv" | "json" | "pdf" = "csv", locale?: string): string {
    // `locale` only changes the words INSIDE the PDF; the server ignores it for csv/json.
    const lang = format === "pdf" && locale ? `&locale=${encodeURIComponent(locale)}` : "";
    return `${API_BASE}/sessions/${sessionId}/export?format=${format}${lang}`;
  },
  /** What happened in a room, after the fact. Host-only and tenant-scoped on the server. */
  async results(sessionId: string): Promise<{ status: number; data: SessionResults | null }> {
    const res = await fetch(`${API_BASE}/sessions/${sessionId}/results`, { credentials: "include" });
    if (!res.ok) return { status: res.status, data: null };
    return { status: res.status, data: (await res.json()) as SessionResults };
  },
  /** Current entitlement + the self-serve catalog. The catalog comes from the server so the
   *  price a buyer sees is the price the server charges — never a number hardcoded here. */
  async billing(): Promise<{ status: number; data: BillingState | null }> {
    const res = await fetch(`${API_BASE}/billing/subscription`, { credentials: "include" });
    if (!res.ok) return { status: res.status, data: null };
    return { status: res.status, data: (await res.json()) as BillingState };
  },
  /** Start a purchase. Returns Mercado Pago's hosted checkout URL to redirect to. */
  checkout(
    plan: string,
    billing: string,
    email: string,
  ): Promise<{ status: number; data: { checkoutUrl?: string; error?: string } }> {
    return postJson("/billing/checkout", { plan, billing, email });
  },
};

export interface PlanOption {
  billing: "monthly" | "annual" | "one_off";
  amount: number;
  durationDays: number | null;
}

export interface BillingState {
  /** The tier actually in force right now — from the same lookup the API uses to gate features. */
  plan: string;
  billable: Array<{ plan: string; options: PlanOption[] }>;
  /** The newest row, whatever its state. Deliberately can disagree with `plan`: a `past_due`
   *  subscription still shows here while the entitlement has already fallen back to free. */
  subscription: {
    id: string;
    plan: string;
    billing: string;
    status: string;
    expiresAt: number | null;
  } | null;
}

/**
 * The server's refusal for "not on this plan", turned into something a screen can render.
 *
 * 402 is the API's chosen code for a quota (as opposed to 403 for a concurrent-room cap, which is
 * a different sentence: "not right now" rather than "not on this plan"). Both route here because
 * both are answered by the same page.
 */
export interface LimitError {
  code: "ROUND_LIMIT_REACHED" | "SESSION_LIMIT_REACHED" | "ASYNC_NOT_ON_PLAN";
  plan: string;
  limit: number;
}

/** The three refusals whose answer is the upgrade page. Kept as data so the banner cannot drift. */
const LIMIT_CODES = new Set<LimitError["code"]>(["ROUND_LIMIT_REACHED", "SESSION_LIMIT_REACHED", "ASYNC_NOT_ON_PLAN"]);

export function asLimitError(status: number, data: unknown): LimitError | null {
  if (status !== 402 && status !== 403) return null;
  const d = data as { error?: string; plan?: string; limit?: number };
  if (!LIMIT_CODES.has(d.error as LimitError["code"])) return null;
  // Defaults only for rendering — a malformed body must still produce a usable prompt rather than
  // "undefined" in the sentence a paying customer reads.
  return { code: d.error as LimitError["code"], plan: d.plan ?? "free", limit: d.limit ?? 0 };
}

/**
 * The server's "that round's window is not honest" (400), turned into which sentence to show (W11).
 *
 * A sibling of `asLimitError` and `asSessionOver` rather than a case inside either: no plan sells a
 * fix for these — the host has to shorten the round, or score it by accuracy. It exists for the same
 * reason those two do: a 400 that nothing recognises renders as NOTHING, so the host clicks "abrir
 * rodada", the screen does not change, and they click again.
 */
export type RoundRefusal =
  | "ROUND_TOO_LONG"
  | "ROUND_OUTLIVES_SESSION"
  | "ASYNC_NEEDS_FLAT_SCORING"
  // US-LV-20's three dial refusals (round-1 review, item J). Same 400, same silence: `validateDials`
  // runs BEFORE the quota, so a host who mis-set a dial pressed "abrir rodada", nothing moved, and
  // the reason was in a response body no screen read. Each names its own control, exactly as the
  // server's codes do — a shared "bad setting" would send them looking at the wrong box.
  | "INVALID_MAX_WORDS"
  | "INVALID_PANEL"
  | "INVALID_REVEAL_TALLY";

const ROUND_REFUSALS = new Set<RoundRefusal>([
  "ROUND_TOO_LONG",
  "ROUND_OUTLIVES_SESSION",
  "ASYNC_NEEDS_FLAT_SCORING",
  "INVALID_MAX_WORDS",
  "INVALID_PANEL",
  "INVALID_REVEAL_TALLY",
]);

export function asRoundRefusal(status: number, data: unknown): RoundRefusal | null {
  if (status !== 400) return null;
  const code = (data as { error?: string }).error;
  return ROUND_REFUSALS.has(code as RoundRefusal) ? (code as RoundRefusal) : null;
}

/**
 * How many people have answered the open round — the numerator of the host's
 * "{answered} de {enrolled} responderam" (W11, AC5).
 *
 * Null where the sentence would be meaningless rather than zero: a ballot counts votes and a draw
 * takes no answer at all, so neither is a count of who did their homework. Mirrors the server's
 * `respondentsIn` for the three types a durable round can be — approved entries only for `open`,
 * exactly as the projection counts them.
 */
export function answeredIn(agg: QuestionAggregate | null | undefined): number | null {
  if (!agg) return null;
  // `respondents` FIRST, and that is the round-1 review's fifth finding: `total` on a word cloud
  // counts words and `entries.length` counts questions, so twelve people writing twenty words made
  // the host's line read "20 de 12 responderam" — not a rounding error, a sentence that cannot be
  // true. `mc` is a `set` per token, so its `total` already means respondents. The `?? total`
  // fallback keeps a frame from an older Worker rendering exactly as it did.
  if (agg.type === "wordcloud") return agg.respondents ?? agg.total;
  if (agg.type === "open") return agg.respondents ?? agg.entries.length;
  if (agg.type === "mc") return agg.total;
  return null;
}

/**
 * The server's participant-text caps (`live/text.ts`), mirrored ONCE for the inputs that must not
 * let someone type past them (US-LV-20 AC4, round-1 review item K).
 *
 * A MIRROR, not the rule: `cleanParticipantText` is the trust boundary and still clamps whatever
 * arrives. What this buys is that the clamp is never SILENT — a phone was letting a panellist type
 * a 300-character answer, sending it, and showing them a sentence cut at 280 with no warning.
 *
 * One object rather than a literal per input, because there were already four scattered ones and
 * the wordcloud/Q&A boxes had none at all — a fifth would have been the drift.
 */
export const TEXT_MAX = { nickname: 40, answer: 120, question: 280 } as const;

/**
 * Is this round withholding the LIST its arm would otherwise carry (US-LV-20 AC5)?
 *
 * One predicate, two readers — `WithheldNote` (what the room sees) and `describeAggregate` (what a
 * screen reader hears) — because they were drifting: the note said "hidden" under words the host
 * had already revealed, and the spoken sentence said "0 perguntas" for a round twelve people had
 * answered (round-1 review, items B and D). Both were reading half of this.
 *
 * The reveal is read off the FRAME. `revealed` is on the word-cloud/Q&A arm whenever the round has
 * the dial (round-2 review, item N), and IT is the answer when present: the emptiness of the list
 * cannot be, because an empty list is also a revealed round with every entry still in review — or
 * with nobody having answered — and there the caption went on promising a result "at the end" of a
 * round the host had already opened up. The empty-list fallback stays for the frame a Worker
 * deployed before this sends, which carries no such key and where the list is the only signal there
 * is.
 *
 * False for `mc` deliberately: its own `tally: "hidden"` arm already says this, in the panel and in
 * the spoken summary, so a second sentence from here would print it twice.
 */
export function isWithheldList(
  question: { revealTally?: boolean } | null | undefined,
  closed: boolean,
  agg: QuestionAggregate | null | undefined,
): boolean {
  if (question?.revealTally !== false || closed) return false;
  if (agg?.type !== "wordcloud" && agg?.type !== "open") return false;
  if (agg.revealed !== undefined) return !agg.revealed;
  return agg.type === "wordcloud" ? agg.words.length === 0 : agg.entries.length === 0;
}

/**
 * A closing instant as a DATE in this phone's own timezone (W11, AC6).
 *
 * Never a countdown: five days of ticking seconds is noise, and the one thing a participant needs is
 * which day they have to have answered by. `Intl` is in every browser this app supports and in node,
 * so there is no formatter to ship.
 *
 * This app's `es-LATAM` is handed to `Intl` as-is: `LATAM` parses as a VARIANT subtag (5 alphanums),
 * so the tag is structurally valid and ECMA-402 cannot throw on it — it resolves to plain `es`,
 * which is the right Spanish. Verified in node's ICU, and it is the same rule in every browser.
 * An invalid instant renders nothing rather than "Invalid Date" on somebody's phone. Belt AND
 * braces on purpose, and the mutation pass says so: `format()` is specified to throw a RangeError on
 * a non-finite time value, so the `Number.isFinite` check is redundant TODAY — it is what keeps the
 * null intentional if the catch below is ever narrowed.
 */
export function closesOn(atMs: number | undefined, locale: string): string | null {
  if (atMs === undefined || !Number.isFinite(atMs)) return null;
  try {
    return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(atMs));
  } catch {
    return null;
  }
}

/**
 * The server's "that room is over" (409), turned into which sentence to show.
 *
 * A sibling of `asLimitError` rather than a case inside it: a limit is an upgrade prompt and this is
 * not — no plan buys a 13th hour. It exists because a 409 that nothing recognises renders as
 * nothing, which is the exact silence `LimitBanner` was added to end: the host clicks "abrir
 * rodada", the screen does not change, and they click again.
 */
export function asSessionOver(status: number, data: unknown): "closed" | "expired" | null {
  if (status !== 409) return null;
  const d = data as { error?: string; expired?: boolean };
  if (d.error !== "SESSION_CLOSED") return null;
  return d.expired ? "expired" : "closed";
}
